import { useEffect, useRef } from "react";
import { useBlocker } from "react-router-dom";
import { useModals } from "src/components/Modals";
import { useToasts } from "src/components/Toasts";
import { useTranslation } from "src/i18n";
import type { ButtonColor, ButtonVariant } from "src/elements/Button";

export type UnsavedChangesSave = () => boolean | Promise<boolean>;

export interface UnsavedChangesSaveAction {
  id: string;
  label: string;
  onSave: UnsavedChangesSave;
  color?: ButtonColor;
  variant?: ButtonVariant;
}

interface Props {
  onSave: UnsavedChangesSave;
  saveActions?: UnsavedChangesSaveAction[];
  description?: string;
  saveFailedTitle?: string;
  /** Also guard navigations that only change the query, for pages that pick their resource by it. */
  blockOnSearchChange?: boolean;
}

export const useUnsavedChangesGuard = (
  isDirty: boolean,
  { onSave, saveActions, description, saveFailedTitle, blockOnSearchChange = false }: Props,
) => {
  const { openConfirmModal } = useModals();
  const { toast } = useToasts();
  const { t } = useTranslation("common");
  const isSavingRef = useRef(false);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty &&
      (currentLocation.pathname !== nextLocation.pathname ||
        (blockOnSearchChange && currentLocation.search !== nextLocation.search)),
  );
  const blockerProceedRef = useRef<() => void>(() => undefined);
  const blockerResetRef = useRef<() => void>(() => undefined);
  const onSaveRef = useRef(onSave);
  const saveActionsRef = useRef(saveActions);
  const toastRef = useRef(toast);
  const textRef = useRef({ description, saveFailedTitle });

  if (blocker.state === "blocked") {
    blockerProceedRef.current = blocker.proceed;
    blockerResetRef.current = blocker.reset;
  }
  onSaveRef.current = onSave;
  saveActionsRef.current = saveActions;
  toastRef.current = toast;
  textRef.current = { description, saveFailedTitle };

  useEffect(() => {
    if (blocker.state !== "blocked") return;

    const currentActions = (): UnsavedChangesSaveAction[] =>
      saveActionsRef.current?.length
        ? saveActionsRef.current
        : [
            {
              id: "save",
              label: t("unsavedChanges.save"),
              onSave: () => onSaveRef.current(),
            },
          ];

    // Look the action up again on click: a failed save can move the page onto
    // another version while this modal stays open.
    const runSave = async (actionId: string) => {
      const action = currentActions().find((candidate) => candidate.id === actionId);
      if (!action || isSavingRef.current) return false;

      isSavingRef.current = true;
      try {
        const didSave = await action.onSave();
        if (didSave) {
          blockerProceedRef.current();
        }
        return didSave;
      } catch (error) {
        toastRef.current({
          title: textRef.current.saveFailedTitle ?? t("unsavedChanges.saveFailed"),
          error,
          type: "ERROR",
        });
        return false;
      } finally {
        isSavingRef.current = false;
      }
    };

    openConfirmModal({
      title: t("unsavedChanges.title"),
      description: textRef.current.description ?? t("unsavedChanges.description"),
      buttons: [
        {
          id: "keep-editing",
          label: t("unsavedChanges.keepEditing"),
          color: "primary",
          variant: "inverted",
          onClick: () => blockerResetRef.current(),
        },
        {
          id: "leave-anyway",
          label: t("unsavedChanges.leaveAnyway"),
          color: "red",
          onClick: () => blockerProceedRef.current(),
        },
        ...currentActions().map((action) => ({
          id: action.id,
          label: action.label,
          color: action.color ?? "primary",
          variant: action.variant,
          onClick: () => runSave(action.id),
        })),
      ],
    });
  }, [blocker.state, openConfirmModal, t]);

  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);
};
